import React, { useEffect } from 'react';
import { pageTitle } from '../../helper';
import Cta from '../Cta';
import FunFact from '../FunFact';
import PageHeading from '../PageHeading';
import Div from '../Div';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';

const getOptimizedImageSources = (src) => {
  if (!src) return null;
  const base = src.replace(/\.(jpe?g|png|gif)$/i, '');
  return {
    avif: `${base}.avif`,
    webp: `${base}.webp`,
    fallback: src,
  };
};

const AboutImage = ({ src, alt, ratio = '4 / 5', sizes = '(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 33vw' }) => {
  const optimized = getOptimizedImageSources(src);

  return (
    <picture>
      <source type="image/avif" srcSet={`${optimized.avif} 900w`} sizes={sizes} />
      <source type="image/webp" srcSet={`${optimized.webp} 900w`} sizes={sizes} />
      <img
        src={optimized.fallback}
        alt={alt}
        loading="lazy"
        decoding="async"
        width="900"
        height="1200"
        sizes={sizes}
        className="w-100 cs-radius_15 cs-about-image"
        style={{ aspectRatio: ratio, objectFit: 'cover' }}
      />
    </picture>
  );
};

const funfaceData = [
  {
    title: 'Global Happy Clients',
    factNumber: '250',
  },
  {
    title: 'Project Completed',
    factNumber: '800',
  },
  {
    title: 'Industries Served',
    factNumber: '30',
  },
  {
    title: 'Client Satisfaction',
    factNumber: '98%',
  },
];

export default function AboutPage() {
  pageTitle('About');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      {/* Start Page Heading Section */}
      <PageHeading
        title="About Us"
        bgSrc="images/about_hero_bg.jpeg"
        pageLinkText="About Us"
      />
      {/* End Page Heading Section */}

      {/* Start About Section */}
      <Spacing lg="150" md="80" />
      <Div className="container">
        <Div className="row">
          <Div className="col-xl-5 col-lg-7">
            <SectionHeading
              title="Where Storytelling Meets Strategy"
              subtitle="About Our Agency"
            >
              <Spacing lg="30" md="20" />
              <p className="cs-m0">
                At Vox Motion, we bring stories to life through powerful, performance-driven video editing. 
                Whether you're a brand looking to grow or a creator ready to inspire, we shape content that 
                connects deeply and delivers results.
                From fast-paced reels to thoughtful documentaries, we help you turn vision into 
                visuals that move audiences and make an impact.
                Because in today’s world, good content isn't enough. 
                You need content that feels something — and gets people to do something.
              </p>
              <Spacing lg="30" md="30" />
              <Div className="cs-separator cs-accent_bg"></Div>
              <Spacing lg="25" md="40" />
            </SectionHeading>
          </Div>
          <Div className="col-lg-5 offset-xl-2">
            <AboutImage src="/images/aboutphoto1.jpg" alt="About" ratio="4 / 5" sizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 33vw" />
            <Spacing lg="25" md="25" />
          </Div>
          <Div className="col-lg-7">
            <AboutImage src="/images/aboutphoto2.jpg" alt="About" ratio="16 / 10" sizes="(max-width: 767px) 100vw, (max-width: 1199px) 70vw, 58vw" />
            <Spacing lg="25" md="25" />
          </Div>
          <Div className="col-lg-5">
            <AboutImage src="/images/aboutphoto3.jpg" alt="About" ratio="4 / 5" sizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 33vw" />
            <Spacing lg="25" md="25" />
          </Div>
        </Div>
      </Div>
      <Spacing lg="75" md="55" />
      {/* End About Section */}

      {/* Start Fun Fact Section */}
      <Div className="container">
        <FunFact
          title="Our fun fact"
          subtitle="Sed ut perspiciatis unde omnis iste natus error voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis."
          data={funfaceData}
        />
      </Div>
      {/* End Fun Fact Section */}

      {/* Start Why Choose Section */}
      <Spacing lg="100" md="80" />
      <Div className="container">
        <Div className="row">
          <Div className="col-xl-5 col-lg-6">
            <Div className="cs-image_layer cs-style1">
              <Div className="cs-image_layer_in">
                <AboutImage src="/images/aboutphoto4.jpg" alt="About" ratio="4 / 5" sizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 42vw" />
              </Div>
            </Div>
            <Spacing lg="0" md="40" />
          </Div>
          <Div className="col-xl-5 offset-xl-1 col-lg-6">
            <SectionHeading
              title="We blend strategy, emotion, and storytelling to create videos that produce results."
              subtitle="Why Choose Us"
            >
              <Spacing lg="30" md="20" />
              <p className="cs-m0">
                At Vox Motion, we use strategy, emotion, and storytelling to craft visually stunning videos that deliver real results. 
                Starting with your goals and audience, we use psychology, creative direction, and years of editing experience to create content that works for you.
                Whether you're a brand, a creator, or a growing business — we can help you save your time and produce top notch content.
              </p>
              <Spacing lg="15" md="15" />
              <p className="cs-m0">
                Lets create something amazing together.
              </p>
              <Spacing lg="30" md="30" />
              <Div className="cs-separator cs-accent_bg"></Div>
              <Spacing lg="25" md="0" />
            </SectionHeading>
          </Div>
        </Div>
      </Div>
      {/* End Why Choose Section */}

      {/* Start CTA Section */}
      <Spacing lg="150" md="80" />
      <Div className="container">
        <Cta
          title="Let’s discuss and make <br />something <i>cool </i> together"
          btnText="Apply For Meeting"
          btnLink="/contact"
          bgSrc="/images/cta_bg.jpeg"
        />
      </Div>
      {/* End CTA Section */}
    </>
  );
}
